import { type FC, useId } from "react";
import { SideDrawer, type SideDrawerControlProps } from "../../../../patterns/side-drawer";
import { Checkbox, ClickableText, Text, Toggle } from "../../../../primitives";
import { useWipColumnSelector } from "./WipColumnSelectorProvider";
import { WIP_ANALYSIS_GRID_METRICS } from "./wipMetric";

export const WIP_COLUMN_SELECTOR_ID = "wip-column-selector";

const columnSelectorIds = (suffix: string) => {
	const id = `${WIP_COLUMN_SELECTOR_ID}-${suffix}`;
	return { "data-testid": id, trackingId: id };
};

export const WipColumnSelectorDrawer: FC<SideDrawerControlProps> = ({
	id,
	isOpen,
	onClose,
	testId = `${WIP_COLUMN_SELECTOR_ID}-drawer`,
	trackingId,
}) => (
	<SideDrawer
		id={id}
		isOpen={isOpen}
		onClose={onClose}
		title="Columns"
		closeLabel="Close column selector"
		testId={testId}
		trackingId={trackingId}
		className="min-h-0"
	>
		<WipColumnSelectorDrawerPanel />
	</SideDrawer>
);

const WipColumnSelectorDrawerPanel: FC = () => {
	const {
		toggleMetric,
		selectAllMetrics,
		clearAllMetrics,
		setComparisonView,
		comparisonView,
		isWipColumnChecked,
	} = useWipColumnSelector();
	const comparisonViewNoteId = useId();

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex flex-col gap-3 border-b border-bc-subtle p-4">
				<Toggle
					checked={comparisonView}
					onChange={setComparisonView}
					label={<Text type="body">Comparison View</Text>}
					{...columnSelectorIds("comparison-view")}
				/>
				{comparisonView && (
					<Text
						id={comparisonViewNoteId}
						type="caption"
						color="tertiary"
						data-testid={`${WIP_COLUMN_SELECTOR_ID}-comparison-view-note`}
					>
						Turn off Comparison View to choose columns.
					</Text>
				)}
			</div>
			<div
				role="group"
				aria-label="Columns"
				aria-describedby={comparisonView ? comparisonViewNoteId : undefined}
				className="flex min-h-0 flex-1 flex-col"
			>
				<div className="flex items-center gap-4 border-b border-bc-subtle px-4 py-3">
					<ClickableText
						type="body"
						color="link"
						disabled={comparisonView}
						onClick={selectAllMetrics}
						{...columnSelectorIds("select-all")}
					>
						Select all
					</ClickableText>
					<ClickableText
						type="body"
						color="link"
						disabled={comparisonView}
						onClick={clearAllMetrics}
						{...columnSelectorIds("deselect-all")}
					>
						Deselect all
					</ClickableText>
				</div>
				<div className="flex-1 space-y-1 overflow-y-auto p-3">
					{WIP_ANALYSIS_GRID_METRICS.map((metric) => {
						const checked = isWipColumnChecked(metric.id);
						return (
							<div
								key={metric.id}
								className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-surface-light"
							>
								<Checkbox
									checked={checked}
									disabled={comparisonView}
									onChange={() => toggleMetric(metric.id)}
									label={<Text type="body">{metric.name}</Text>}
									{...columnSelectorIds(`checkbox-${metric.id}`)}
									className="shrink-0"
								/>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};
